/* eslint-disable promise/always-return */
import moment from 'moment';
import Axios, { AxiosResponse, CancelTokenSource } from 'axios';
import { GetTransactions } from '../../../services/types';
import { TransactionType } from '../../../entities/transactions-v1/query-types';
import Transactions from '../../../services/transactions';
import { QueryParameterKeys } from '../../../globals/paramter';
import { Result } from '../../../store/search/types';
import { UserPreference } from '../../../store/user-preferences-v2/types';
import { LoginState } from '../../../store/login/types';
import { findPrioritizedIndex, formatDate } from '../../../globals/utils';
import Assets from '../../../services/assets-v2';
import GetCommandById from '../../../services/commands';
import ConfigurationFilesLibrary from '../../../services/config-files-library';

let ajaxRequest: CancelTokenSource | null = null;

type SortingDirection = 'asc' | 'desc';
interface Sorting {
  direction: SortingDirection;
  columnName: string;
}


export class TransactionsFetch {
  private queryParameters: URLSearchParams;

  private selectedEntity: Result | undefined;

  private userPreferences: UserPreference;

  private login: LoginState;

  private sortBy: Sorting | null;

  constructor(
    queryParameters: URLSearchParams,
    selectedEntity: Result | undefined,
    userPreferences: UserPreference,
    login: LoginState,
    sortBy: Sorting | null,
  ) {
    this.queryParameters = queryParameters;

    this.selectedEntity = selectedEntity;

    this.userPreferences = userPreferences;

    this.login = login;

    this.sortBy = sortBy;

  }

  data = async (
    pageSize: number,
    nextCursor: string,
    startTime?: string,
    endTime?: string,
  ): Promise<any> => {
    if (ajaxRequest) {
      ajaxRequest.cancel();
    }
    const parameters = new URLSearchParams(this.queryParameters);
    const category = parameters.get(QueryParameterKeys.category) || undefined;
    const fileTypeIdFilters = parameters.get(QueryParameterKeys.fileTypeIdFilters) || undefined;
    const excludeFileTypeIdFilters = parameters.get(QueryParameterKeys.excludeFileTypeIdFilters) || undefined;
    const vimsData = parameters.get(QueryParameterKeys.vimsData) || undefined;
    const viewFileSize = parameters.get(QueryParameterKeys.viewFileSize) || undefined;

    const transactionTypes =
      category ||
      [TransactionType.message, TransactionType.command, TransactionType.dataFile].toString();

    ajaxRequest = Axios.CancelToken.source();
    const deviceKey = this.selectedEntity?.deviceKey || undefined;
    const telematicsAssetId = this.selectedEntity?.telematicsAssetId || undefined;
    const transactionsRequest: GetTransactions = {
      startTime,
      endTime,
      telematicsAssetId: vimsData ? telematicsAssetId : undefined,
      deviceKey: !vimsData ? deviceKey : undefined,
      limit: pageSize,
      fileTypeIdFilters,
      excludeFileTypeIdFilters,
      orderBy: this.sortBy?.direction && this.sortBy.direction,
      sortBy: this.sortBy?.columnName ? [this.sortBy.columnName] : undefined,
      transactionTypes,
      viewFileSize: viewFileSize === undefined ? 'false' : viewFileSize,
    };

    const { client, envVariables } = this.login;

    const transactionsService = new Transactions(client, envVariables.transactionsV1BaseUrl);

    if (nextCursor !== '') transactionsRequest.cursor = nextCursor;
    const response = await transactionsService.getAll(transactionsRequest, ajaxRequest.token);
    let transactions;
    if (response) {
      transactions = response.data.transactions.transaction;
      const cursor = response.data.responseTransaction.nextCursor;
      const { timeFormat, dateFormat } = this.userPreferences.globalUserPreferences;
      const getEndpointOrCommandTs = (transaction: any) => {
        if (transaction.routes) {
          const timeIndex = findPrioritizedIndex(transaction.routes, 'downstreamSystem', [
            'DataHub',
            'CCDS',
            'ODS',
          ]);
          if (timeIndex !== -1) {
            return moment(transaction.routes[timeIndex].sentTime)
              .utc()
              .format(formatDate(dateFormat, timeFormat));
          }
        }
        return 'N/A';
      };
      const telematicAssetIds: { [index: string]: string } = {};
      transactions.forEach((transaction: any) => {
        telematicAssetIds[transaction.telematicsAssetId] = '';
      });
      const requests: Array<Promise<any>> = [];
      const assets = new Assets(client, envVariables.assetsV2BaseUrl);

      const retrieveAssets = async (id: string) => {
        try {
          return assets.getByTelematicsAssetId(id);
        } catch {
          return [];
        }
      };

      const getGatewayOrRegion = (gatewayNameAndRegion: string | undefined, name: string) => {
        let gatewayOrRegion;
        if (gatewayNameAndRegion) {
          if (name === "gateway") {
            gatewayOrRegion = gatewayNameAndRegion.slice(0, gatewayNameAndRegion.lastIndexOf("_"));
          } else if (name === "region") {
            gatewayOrRegion = gatewayNameAndRegion.split('_')[gatewayNameAndRegion.split('_').length - 1];
          }
        }
        if (gatewayOrRegion) { return gatewayOrRegion; }
        return 'N/A';
      };
      Object.keys(telematicAssetIds).forEach((id: string) => {
        if (id && id !== 'undefined') {
          requests.push(retrieveAssets(id));
        }
      });
      Promise.all(requests)
        .then((response: any) => {
            telematicAssetIds[response.data.telematicsAssetId] = response.data.assetSerialNumber;
        })
        // eslint-disable-next-line no-console
        .catch((error) => console.error(`Non-blocking request failure. ${error.message}`));
      const configFilesLibrary = new ConfigurationFilesLibrary(
        client,
        envVariables.configurationFilesLibraryV1BaseUrl,
      );
      const commandService = new GetCommandById(client, envVariables.commandsV1BaseUrl);
      const fileConfigDataRequests = (transactions || []).map(async (transaction: any) => {
        if (transaction.fileTypeName?.split(' ').pop()?.toLowerCase() === 'file') {
          try {
            const commandDetails: AxiosResponse<any> = await commandService.getCommandById({
              commandId: transaction.id,
            });
            if (!commandDetails.data.hash) {
              return null;
            }
            const libraryResponse = await configFilesLibrary.getConfigFileMetaData(
              commandDetails.data.hash,
            );
            return {
              id: transaction.id,
              fileName: libraryResponse.data.fileName,
            };
          } catch {
            return null;
          }
        }
        return null;
      });

      const fileConfigData = await Promise.all(fileConfigDataRequests);

      const formattedTransactions = transactions
        ? transactions.map((transaction: any) => ({
            id: transaction.id,
            deviceId: this.selectedEntity?.deviceId,
            assetSerialNumber: transaction.assetSerialNumber,
            transactionType:
              transaction.transactionType === 'PLMESSAGE' ? 'MESSAGE' : transaction.transactionType,
            description: transaction.fileTypeName,
            fileName: transaction.fileName,
            fileTypeId: transaction.fileTypeId,
            isProtoParsingSupported: transaction.isProtoParsingSupported,
            applicationId: transaction.applicationId ? transaction.applicationId : 'N/A',
            messageCreationTime:
              transaction.messageCreationTime && transaction.messageCreationTime.trim() !== ''
                ? moment(transaction.messageCreationTime)
                    .utc()
                    .format(formatDate(dateFormat, timeFormat))
                : 'N/A',
            gatewayReceiptTime:
              transaction.gatewayReceiptTime && transaction.gatewayReceiptTime.trim()
                ? moment(transaction.gatewayReceiptTime)
                    .utc()
                    .format(formatDate(dateFormat, timeFormat))
                : 'N/A',
            status: transaction.status,
            endpointOrCommandTs: getEndpointOrCommandTs(transaction),
            deviceType: transaction.deviceType,
            createdBy: transaction.createdBy,
            commandStatusTimestamp:
              transaction.lastUpdateTime && transaction.lastUpdateTime.trim()
                ? moment(transaction.lastUpdateTime)
                    .utc()
                    .format(formatDate(dateFormat, timeFormat))
                : 'N/A',
            removedBy:
              transaction.status === 'REMOVED_DUE_TO_INACTIVITY' ||
              transaction.status === 'REMOVED_FROM_QUEUE' ||
              transaction.status === 'REMOVED_FROM_QUEUE_DUPLICATE'
                ? transaction.lastUpdatedBy
                : 'N/A',
            replayCount: transaction.replays?.replayCount || 0,
            transmissionToCatMQ: getEndpointOrCommandTs(transaction),
            lastResubmissionUserId: transaction.replays?.replayUserId || 'N/A',
            deviceKey: this.selectedEntity?.deviceKey,
            lastUpdateTime:
              transaction.lastUpdateTime && transaction.lastUpdateTime.trim()
                ? moment(transaction.lastUpdateTime)
                    .utc()
                    .format(formatDate(dateFormat, timeFormat))
                : 'N/A',
          gatewayName: getGatewayOrRegion(transaction?.gatewayNameAndRegion, "gateway"),
          region: getGatewayOrRegion(transaction?.gatewayNameAndRegion, "region"),
            fileTypeName: (() => {
              if (transaction.transactionType === 'DATAFILE') {
                return transaction.fileName;
              }
              if (transaction.fileTypeName?.split(' ').pop()?.toLowerCase() === 'file') {
                return (
                  fileConfigData.find((configData) => configData?.id === transaction.id)
                    ?.fileName || transaction.createdBy
                );
              }
              if (transaction.transactionType === 'COMMAND') {
                return transaction.createdBy;
              }
              return '';
            })(),
            fileSize:viewFileSize ? transaction.fileSize || "N/A" : "",
            telematicsAssetId: this.selectedEntity?.telematicsAssetId,
            extension: transaction.extension,
          }))
        : [];
      const processedDataPromise: Promise<any> = new Promise((resolve) => {
        resolve({ data: formattedTransactions, cursor });
      });
      return processedDataPromise;
    }
    return new Promise((resolve) => {
      resolve({ data: [], cursor: undefined });
    });
  };
}
